import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { ApplicationState } from 'store/rootReducer';
import { AnyAction } from 'redux';
import _ from 'lodash';
import {
  AnalyticsDataState,
  MsAnalyticsDataState,
  StateChangesState,
  TabDataState,
} from 'store/analytics/data/analyticsDataTypes';
import {
  copyDataState,
  fetchAnalyticsInspectionDataDetail,
  fetchStateChangesInspectionData,
  isTabActive,
  updateCurrentDataContext,
  updateTabData,
} from 'store/analytics/data/analyticsDataActions';
import { BlazeMeterApiResponse } from 'types/rest/blazeMeterApiResponse';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import { action } from 'typesafe-actions';
import {
  STATE_CHANGES_SET_IS_REQUEST_SENT,
  STATE_CHANGES_SET_VIEW_IS_LOADING_STATES,
  STATE_CHANGES_VIEW_UPDATE,
} from 'store/analytics/data/analyticsDataActionTypes';
import { StateChangesType } from 'types/rest/analyticsTypes';
import {
  selectStateChangesViewTriggered,
  setLoadingStateChangesDetail,
} from 'store/analytics/ui/subViews/stateChangesView/stateChangesViewActions';

export const setStateChangesRequestSent = (isStateChangesRequested: boolean) =>
  action(STATE_CHANGES_SET_IS_REQUEST_SENT, isStateChangesRequested);
export const setLoadingStateChanges = (isLoading: boolean) =>
  action(STATE_CHANGES_SET_VIEW_IS_LOADING_STATES, isLoading);
export const updateCurrentStateChangesViewData = (stateChangesViewData: StateChangesState) =>
  action(STATE_CHANGES_VIEW_UPDATE, stateChangesViewData);

const setStateChangesDetailOutdated = (
  getState: any,
  tabId: number | string,
  dispatch: ThunkDispatch<ApplicationState, null, AnyAction>,
) => {
  const currentDataState: MsAnalyticsDataState = copyDataState(getState).activeDataContext;
  // Check if we are in same data context
  if (isTabActive(tabId)) {
    currentDataState.stateChanges.isLoadingDetails = false;
    currentDataState.stateChanges.isOutdated = true;
    dispatch(updateCurrentDataContext(currentDataState));
  }
};

/**
 * Select stateChanges in the stateChanges table. Marks the item in the table as selected and reloads
 * detail of the stateChange.
 * @param stateChangesId - id of the stateChanges to be loaded
 * @param tabId - id of the tab which stateChanges belongs to
 */
export const fetchStateChangesDetailThunk = (
  stateChangesId: number,
  tabId: number | string | null,
): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    //Do not continue if tab / stateChanges id is empty
    if (_.isNil(tabId) || _.isNil(stateChangesId)) {
      return;
    }

    //Add selected stateChanges to the ui state
    const tabData: TabDataState[] = getState().analytics.data.tabData;
    const tab = tabData.find((item: TabDataState) => String(item.tabId) === String(tabId));
    const stateChangesType: StateChangesType = tab.stateChanges.stateChangesTypes.find(
      (stateChanges: any) => stateChanges.id === stateChangesId,
    );

    if (isTabActive(tabId)) {
      await dispatch(selectStateChangesViewTriggered(stateChangesType));
      await dispatch(setLoadingStateChangesDetail(true));
    }

    try {
      const response: BlazeMeterApiResponse = (await fetchAnalyticsInspectionDataDetail(stateChangesType.id)).data;
      const updatedTab = _.cloneDeep(tab);
      updatedTab.stateChanges.detail = response.result.parameters;
      updatedTab.stateChanges.filters = response.result.filters;
      updatedTab.stateChanges.changedRowsCount = response.result.changedRowsCount;
      updatedTab.stateChanges.isLoadingDetails = false;
      updatedTab.stateChanges.isOutdated = false;
      updatedTab.stateChanges.isDetailRequested = true;

      // Update data
      dispatch(updateTabData(updatedTab));

      //Is tab still active, update current inspection view
      if (isTabActive(tabId)) {
        dispatch(updateCurrentStateChangesViewData(updatedTab.stateChanges));
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setStateChangesDetailOutdated(getState, tabId, dispatch);
      } else {
        const errorBody = _.get(error, 'response.data.error', error.toString());
        dispatch(showToastNotification(ToastType.Danger, 'Cannot retrieve state changes detail data', errorBody));
      }
    }
  };
};

export const fetchStateChangesThunk = (
  mockServiceId: number | string | null,
): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    if (_.isNil(mockServiceId)) {
      console.error('No virtual service id available.');
      return;
    }

    // Set loading to true in current stateChanges view
    await dispatch(setStateChangesRequestSent(true));
    await dispatch(setLoadingStateChanges(true));
    const dataState: AnalyticsDataState = copyDataState(getState);

    try {
      const response: BlazeMeterApiResponse = (await fetchStateChangesInspectionData(mockServiceId)).data;
      const stateChanges = response.result;

      const activeDataContext = dataState.activeDataContext as MsAnalyticsDataState;
      activeDataContext.stateChanges.stateChangesTypes = stateChanges;
      activeDataContext.stateChanges.isLoadingData = false;
      activeDataContext.stateChanges.lastRefreshTs = new Date().getTime();

      // Save result to data array
      dispatch(updateTabData(activeDataContext));

      // Update current data context (if still active)
      if (isTabActive(mockServiceId)) {
        await dispatch(updateCurrentStateChangesViewData(activeDataContext.stateChanges));
      }

      //Preselect first item and do the reload of details
      if (stateChanges.length !== 0) {
        const firstRowId = parseInt(stateChanges[0].id);
        dispatch(fetchStateChangesDetailThunk(firstRowId, mockServiceId));
      }
    } catch (error) {
      const errorBody = _.get(error, 'response.data.error', error.toString());
      dispatch(showToastNotification(ToastType.Danger, 'Cannot retrieve analytics data', errorBody));
      if (isTabActive(mockServiceId)) {
        dispatch(setLoadingStateChanges(false));
      }
    }
  };
};
